import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Быстрая заметка: плавающая кнопка, видимая на любой странице, + горячая клавиша N.
// По умолчанию цепляет заметку к сущности, открытой на экране (сделка/контакт/компания);
// на списках создаётся заметка без привязки — «просто запись в workspace».
//
// Сама форма создания живёт в NoteCreateModal — её же открывает кнопка
// «Новая заметка» в списке заметок.
import { useEffect, useState, type ReactElement } from 'react';
import { Button, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';

import { useCurrentSelection } from '../../../shared/selection/useSelectionStore.js';
import { useCurrentEntityContext } from '../hooks/useCurrentEntityContext.js';
import { NoteCreateModal } from './NoteCreateModal.js';

/** Не перехватываем клавишу, когда пользователь печатает. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
}

export function QuickNoteButton(): ReactElement {
  useI18nSubscription();
  const entity = useCurrentEntityContext();
  const selection = useCurrentSelection();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || isTypingTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Physical N key: layout- and locale-independent ('n'/'N' on QWERTY and
      // 'т'/'Т' on ЙЦУКЕН share the same code). Matching event.key against a
      // translated string broke the hotkey in EN and hijacked Shift+T.
      if (event.code === 'KeyN') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const hasEntity = entity.kind !== null;
  // Карточка не открыта, но в списке отмечены строки — цепляем к ним.
  const hasSelection = !hasEntity && selection.kind !== null && selection.items.length > 0;
  const buttonLabel = hasEntity
    ? i18n.t('crm.features.dynamic.features.notes.components.quicknotebutton.68', {
        p0: entity.typeLabel,
      })
    : hasSelection
      ? i18n.t('crm.features.dynamic.features.notes.components.quicknotebutton.69', {
          p0: selection.items.length,
        })
      : i18n.t('crm.features.features.notes.components.quicknotebutton.быстрая_заметка');

  return (
    <>
      <Tooltip
        title={i18n.t('crm.features.dynamic.features.notes.components.quicknotebutton.70', {
          p0: buttonLabel,
        })}
        placement="left"
      >
        <Button
          type="default"
          shape="round"
          size="large"
          icon={<EditOutlined />}
          aria-label={buttonLabel}
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 1000,
            boxShadow: '0 6px 20px rgba(217, 119, 6, 0.35)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {i18n.t('crm.features.features.notes.components.quicknotebutton.заметка')}
        </Button>
      </Tooltip>

      <NoteCreateModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
